import { Segment } from '@mairistem/composants';
import { type Exercice, Sens } from '@mairistem/comptabilite';
import { Combobox, getErrorMessage } from '@mairistem/finances-utils';
import { isArray, isEmpty } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type BordereauStateType from '../../../../constants/liquidation/bordereaux/BordereauStateType';
import type SensOptions from '../../../../interfaces/pieces/bordereaux/SensOptions';
import { SENS_TOUS } from '../../../../interfaces/pieces/bordereaux/SensOptions';
import { getExercices } from '../../../../services/entites/exercices';
import useLiquidationTreeviewStore from '../../../../stores/liquidationTreeviewStore';
import FiltresBordereauState from '../Bordereau/FiltresBordereauState';
import './FiltersTreeview.css';

interface FiltersTreeviewProps {
	onAnneeChange: (anneeObject: number) => void;
	onSensChange: (sens: SensOptions) => void;
	onFiltreBordereauChange: (bordereauStateType: number) => void;
	selectedAnnee: number;
	selectedSens: SensOptions;
	selectedBordereauState: number | null;
	visibleStates: Set<BordereauStateType> | null;
}

const FILTRE_SENS_OPTIONS = [
	{
		key: SENS_TOUS,
		text: 'Dépenses/Recettes',
		value: SENS_TOUS,
		'data-testid': 'filtreSensComboboxOptionTous',
	},
	{
		key: Sens.DEPENSE,
		text: 'Dépenses',
		value: Sens.DEPENSE,
		'data-testid': 'filtreSensComboboxOptionDepense',
	},
	{
		key: Sens.RECETTE,
		text: 'Recettes',
		value: Sens.RECETTE,
		'data-testid': 'filtreSensComboboxOptionRecette',
	},
];

const FiltersTreeview = ({
	onAnneeChange,
	onSensChange,
	onFiltreBordereauChange,
	selectedAnnee,
	selectedSens,
	selectedBordereauState,
	visibleStates,
}: FiltersTreeviewProps) => {
	// années chargées une seule fois et gardées dans le store, comme l'année choisie
	const exercices = useLiquidationTreeviewStore((state) => state.exercices);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de +
	const fetchExercices = React.useCallback(async () => {
		if (useLiquidationTreeviewStore.getState().exercices) return;
		try {
			const { data } = await getExercices(
				new URLSearchParams({
					'distincts[]': 'exe_annee',
					'columns[]': 'exe_annee',
				}),
			);
			const sortedData: Exercice[] = data.sort(
				(a: Partial<Exercice>, b: Partial<Exercice>) =>
					b.anneeExercice - a.anneeExercice,
			);

			if (isArray(sortedData) && !isEmpty(sortedData)) {
				useLiquidationTreeviewStore.getState().setExercices(sortedData);
				if (
					useLiquidationTreeviewStore.getState().anneeExercice === undefined
				) {
					onAnneeChange(sortedData[0]?.anneeExercice);
				}
			}
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors de la récupération des exercices'),
			);
		}
	}, []);

	const handleAnneeChange = React.useCallback(
		(_, { value }: { value: string }) => {
			onAnneeChange(Number(value));
		},
		[onAnneeChange],
	);

	const handleSensChange = React.useCallback(
		(_, { value }: { value: SensOptions }) => {
			onSensChange(value);
		},
		[onSensChange],
	);

	React.useEffect(() => {
		fetchExercices();
	}, [fetchExercices]);

	return (
		<Segment>
			<div className="filtres-mouvements-container">
				<Combobox
					options={
						exercices?.map((exercice) => ({
							key: exercice.anneeExercice,
							text: `Exercice ${exercice.anneeExercice} - Tous les budgets`,
							value: exercice.anneeExercice,
							'data-testid': `filtreExerciceComboboxOption${exercice.anneeExercice}`,
						})) || []
					}
					placeholder="Exercice"
					value={selectedAnnee}
					onChange={handleAnneeChange}
					data-testid="filtreExerciceCombobox"
				/>
				<Combobox
					options={FILTRE_SENS_OPTIONS}
					placeholder="Sens"
					onChange={handleSensChange}
					value={selectedSens}
					data-testid="filtreSensCombobox"
				/>
				<FiltresBordereauState
					onChange={onFiltreBordereauChange}
					value={selectedBordereauState}
					visibleStates={visibleStates}
				/>
			</div>
		</Segment>
	);
};

export default FiltersTreeview;
